// type ColorProperty = 'red' | 'green' | 'blue' | 'hue' | 'saturation' | 'lightness' | 'alpha';
interface HSLColor {
	hue: number;
	saturation: number;
	lightness: number;
	alpha: number;
}
interface RGBColor {
	red: number;
	green: number;
	blue: number;
	alpha: number;
}
type RgbUpdates = Partial<RGBColor>;
type HslUpdates = Partial<HSLColor>;
type ColorUpdate = RgbUpdates | HslUpdates;

export class ColorService {
	getBrightness(color: string): number {
		const { red, green, blue } = this.extractRGBColor(color);
		return (red * 299 + green * 587 + blue * 114) / 1000;
	}

	private extractRGBColor(value: string): RGBColor {
		const color = value.toLowerCase();
		if (color.startsWith('rgb')) {
			return this.rgbStringToRGB(color);
		}
		if (color.startsWith('hsl')) {
			return this.hslToRgb(this.hslStringToHsl(color));
		}
		if (color.startsWith('#')) {
			return this.hexStringToRGB(color);
		}

		return this.rgbStringToRGB(this.nameToRGBString(color));
	}

	private rgbStringToRGB(str: string): RGBColor {
		const values = this.extractNumbers(str);
		return {
			red: values[0],
			green: values[1],
			blue: values[2],
			alpha: values[3] / 100,
		};
	}

	private extractNumbers(color: string): number[] {
		const matchRes = color.match(/\d+/g) || [];
		return matchRes.map((v) => +v);
	}

	public isHexColor(color: string): { isRGB: boolean; isRGBA: boolean } {
		const exRGB = /^#([\da-f]{3}){1,2}$/i;
		const exRGBA = /^#([\da-f]{4}){1,2}$/i;

		const isRGB = exRGB.test(color);
		const isRGBA = isRGB ? false : exRGBA.test(color);
		return { isRGB, isRGBA };
	}

	private hexStringToRGB(h: string): RGBColor {
		const { isRGB, isRGBA } = this.isHexColor(h);
		if (isRGB || isRGBA) {
			let red = 0;
			let green = 0;
			let blue = 0;

			// 3-4 digits
			if (h.length <= 5) {
				red = +('0x' + h[1] + h[1]);
				green = +('0x' + h[2] + h[2]);
				blue = +('0x' + h[3] + h[3]);
				// 6-8 digits
			} else {
				red = +('0x' + h[1] + h[2]);
				green = +('0x' + h[3] + h[4]);
				blue = +('0x' + h[5] + h[6]);
			}

			let alpha = 1;
			if (isRGBA) {
				alpha = h.length <= 5 ? +('0x' + h[4] + h[4]) : +('0x' + h[7] + h[8]);
				alpha = +(alpha / 255).toFixed(3);
			}
			return { red, green, blue, alpha };
		} else {
			throw new Error(`Invalid input color: ${h}`);
		}
	}

	private toRGBString({ red, green, blue, alpha }: RGBColor) {
		return `rgb(${red}, ${green}, ${blue}${alpha !== 1 ? '' : ` / ${Math.round(alpha * 100)}%`})`;
	}

	private hslToRgb({ hue, saturation, lightness, alpha }: HSLColor): RGBColor {
		const scaledHue = hue / 360;
		const scaledSaturation = saturation / 100;
		const scaledLightness = lightness / 100;

		const m2 = scaledLightness <= 0.5 ? scaledLightness * (scaledSaturation + 1) : scaledLightness + scaledSaturation - scaledLightness * scaledSaturation;
		const m1 = scaledLightness * 2 - m2;

		return {
			red: clamp(fuzzyRound(this.hueToRgb(m1, m2, scaledHue + 1 / 3) * 255), 0, 255),
			green: clamp(fuzzyRound(this.hueToRgb(m1, m2, scaledHue) * 255), 0, 255),
			blue: clamp(fuzzyRound(this.hueToRgb(m1, m2, scaledHue - 1 / 3) * 255), 0, 255),
			alpha: alpha,
		};
	}

	public hslToHexString(color: HSLColor) {
		return this.rgbToHexString(this.hslToRgb(color));
	}

	private hslStringToHsl(hslArg: string): HSLColor {
		const exHSL =
			/^hsl\(((((([12]?[1-9]?\d)|[12]0\d|(3[0-5]\d))(\.\d+)?)|(\.\d+))(deg)?|(0|0?\.\d+)turn|(([0-6](\.\d+)?)|(\.\d+))rad)((,\s?(([1-9]?\d(\.\d+)?)|100|(\.\d+))%){2}|(\s(([1-9]?\d(\.\d+)?)|100|(\.\d+))%){2})\)$/i;
		const exHSLA =
			/^hsl\(((((([12]?[1-9]?\d)|[12]0\d|(3[0-5]\d))(\.\d+)?)|(\.\d+))(deg)?|(0|0?\.\d+)turn|(([0-6](\.\d+)?)|(\.\d+))rad)(((,\s?(([1-9]?\d(\.\d+)?)|100|(\.\d+))%){2},\s?)|((\s(([1-9]?\d(\.\d+)?)|100|(\.\d+))%){2}\s\/\s))((0?\.\d+)|[01]|(([1-9]?\d(\.\d+)?)|100|(\.\d+))%)\)$/i;

		const isHSL = exHSL.test(hslArg);
		const isHSLA = isHSL ? false : exHSLA.test(hslArg);

		if (isHSL || isHSLA) {
			const sep = hslArg.indexOf(',') > -1 ? ',' : ' ';
			const hsl = hslArg.substr(4).split(')')[0].split(sep);

			// strip the slash
			if (isHSLA && hsl.indexOf('/') > -1) {
				hsl.splice(3, 1);
			}

			let hue = 0;
			const saturation = +hsl[1].substr(0, hsl[1].length - 1);
			const lightness = +hsl[2].substr(0, hsl[2].length - 1);
			let alpha = 1;

			// strip label and convert to degrees (if necessary)
			if (hsl[0].indexOf('deg') > -1) {
				hue = +hsl[0].substr(0, hsl[0].length - 3);
			} else if (hsl[0].indexOf('rad') > -1) {
				hue = Math.round(+hsl[0].substr(0, hsl[0].length - 3) * (180 / Math.PI));
			} else if (hsl[0].indexOf('turn') > -1) {
				hue = Math.round(+hsl[0].substr(0, hsl[0].length - 4) * 360);
			} else {
				hue = +hsl[0];
			}
			if (hue >= 360) {
				hue %= 360;
			}

			// strip % from alpha, make fraction of 1 (if necessary)
			if (isHSLA) {
				if (hsl[3].indexOf('%') > -1) {
					alpha = +hsl[3].substr(0, hsl[3].length - 1) / 100;
				} else {
					alpha = +hsl[3];
				}
			}

			return { hue, saturation, lightness, alpha };
		} else {
			throw new Error('Invalid input color');
		}
	}

	private hslStringToHex(hslArg: string): string {
		return this.hslToHexString(this.hslStringToHsl(hslArg));
	}

	private positiveMod(dividend: number, modulus: number) {
		const result = dividend % modulus;
		return result < 0 ? result + modulus : result;
	}

	public extractHSL(color: string): HSLColor {
		const { red, green, blue, alpha } = this.extractRGBColor(color);
		const scaledRed = red / 255;
		const scaledGreen = green / 255;
		const scaledBlue = blue / 255;

		const max = Math.max(scaledRed, scaledGreen, scaledBlue);
		const min = Math.min(scaledRed, scaledGreen, scaledBlue);
		const delta = max - min;

		let hue: number;
		if (max === min) {
			hue = 0;
		} else if (max === scaledRed) {
			hue = this.positiveMod((60 * (scaledGreen - scaledBlue)) / delta, 360);
		} else if (max === scaledGreen) {
			hue = this.positiveMod(120 + (60 * (scaledBlue - scaledRed)) / delta, 360);
		} /* if (max === scaledBlue)*/ else {
			hue = this.positiveMod(240 + (60 * (scaledRed - scaledGreen)) / delta, 360);
		}

		const lightness = 50 * (max + min);

		let saturation;
		if (max === min) {
			saturation = 0;
		} else if (lightness < 50) {
			saturation = (100 * delta) / (max + min);
		} else {
			saturation = (100 * delta) / (2 - max - min);
		}

		return {
			hue,
			lightness,
			saturation,
			alpha,
		};
	}

	getLightness(color: string): number {
		return this.extractHSL(color).lightness;
	}

	getSaturation(color: string): number {
		return this.extractHSL(color).saturation;
	}

	getHue(color: string): number {
		return this.extractHSL(color).hue;
	}

	private nameToRGBString(name: string): string {
		// create fake div
		const fakeDiv = document.createElement('div');
		fakeDiv.style.color = name;
		document.body.appendChild(fakeDiv);

		// get color of div
		const cs = window.getComputedStyle(fakeDiv),
			pv = cs.getPropertyValue('color');

		// remove div after obtaining desired color value
		document.body.removeChild(fakeDiv);

		return pv;
	}

	mixColors(color1: string, color2: string, weight = 0.5): string {
		const normalizedWeight = weight * 2 - 1;
		const rgb1 = this.extractRGBColor(color1);
		const rgb2 = this.extractRGBColor(color2);

		const alphaDistance = (rgb1.alpha || 1) - (rgb2.alpha || 1);

		const combinedWeight1 = normalizedWeight * alphaDistance === -1 ? normalizedWeight : (normalizedWeight + alphaDistance) / (1 + normalizedWeight * alphaDistance);
		const weight1 = (combinedWeight1 + 1) / 2;
		const weight2 = 1 - weight1;

		const newColor: RGBColor = {
			red: fuzzyRound(rgb1.red * weight1 + rgb2.red * weight2),
			green: fuzzyRound(rgb1.green * weight1 + rgb2.green * weight2),
			blue: fuzzyRound(rgb1.blue * weight1 + rgb2.blue * weight2),
			alpha: (rgb1.alpha || 1) * weight + (rgb2.alpha || 1) * (1 - weight),
		};
		return this.rgbToHexString(newColor);
	}

	rgbToHexString({ red, green, blue, alpha }: RGBColor): string {
		const toHex = (color: number) => Math.round(color).toString(16).padStart(2, '0');
		return `#${toHex(red)}${toHex(green)}${toHex(blue)}${alpha !== 1 ? toHex(alpha * 255) : ''}`;
	}

	private hwbToRgb(hue: number, scaledWhiteness: number, scaledBlackness: number) {
		const factor = 1 - scaledWhiteness - scaledBlackness;
		const channel = this.hueToRgb(0, 1, hue) * factor + scaledWhiteness;
		return fuzzyRound(channel * 255);
	}

	// An algorithm from the CSS3 spec: http://www.w3.org/TR/css3-color/#hsl-color.
	private hueToRgb(m1: number, m2: number, hue: number): number {
		if (hue < 0) hue += 1;
		if (hue > 1) hue -= 1;

		if (hue < 1 / 6) {
			return m1 + (m2 - m1) * hue * 6;
		} else if (hue < 1 / 2) {
			return m2;
		} else if (hue < 2 / 3) {
			return m1 + (m2 - m1) * (2 / 3 - hue) * 6;
		} else {
			return m1;
		}
	}

	getLuminanace(color: string): number {
		const { red, green, blue } = this.extractRGBColor(color);
		const calc = (v: number) => {
			const val = v / 255;
			return val <= 0.03928 ? val / 12.92 : ((val + 0.055) / 1.055) ** 2.4;
		};
		return 0.2126 * calc(red) + 0.7152 * calc(green) + 0.0722 * calc(blue);
	}

	getContrastRatio(colorA: string, colorB: string): number {
		const lumA = this.getLuminanace(colorA);
		const lumB = this.getLuminanace(colorB);
		return (Math.max(lumA, lumB) + 0.05) / (Math.min(lumA, lumB) + 0.05);
	}

	enhanceColorContrast(color: string, bgColor?: string, ratio = 4.5): string {
		// eslint-disable-next-line @typescript-eslint/no-explicit-any
		const bg = bgColor || (mx as any).currentThemeColorsObj?.['content-bg-color'] || '#FFFFFF';

		const step = this.getLuminanace(color) <= this.getLuminanace(bg) ? -0.02 : 0.02;
		let result = color;
		let i = 0;

		while (this.getContrastRatio(bg, result) < ratio && i < 100) {
			i++;
			result = this.scaleColor(result, { lightness: step });
		}
		return result;
	}

	getProperContrastTextColor(color: string, edge = 70) {
		return this.getLightness(color) >= edge ? '#000000' : '#FFFFFF';
	}

	getContrastDelta(color1: string, color2: string) {
		return Math.abs(this.getLightness(color1) - this.getLightness(color2));
	}

	scaleColor(color: string, updates: ColorUpdate) {
		return this.updateColor(color, updates, { scale: true });
	}

	adjustColor(color: string, updates: ColorUpdate) {
		return this.updateColor(color, updates, { adjust: true });
	}

	private updateColor(color: string, updates: ColorUpdate, { adjust, change }: { scale?: boolean; change?: boolean; adjust?: boolean }): string {
		const updateValue = (current: number, param: number, max: number): number => {
			if (param === null) return current;
			if (change) return param;
			if (adjust) return clamp(current + param, 0, max);
			return current + (param > 0 ? max - current : current) * param; //scale (param / 100)
		};

		const updateAlpha = (current: number, param?: number) => (typeof param === 'number' ? updateValue(current, param, 1) : current);
		const updateHSL = (current: number, param?: number) => (typeof param === 'number' ? updateValue(current, param, 100) : current);
		const updateRgb = (current: number, param?: number) => (typeof param === 'number' ? fuzzyRound(updateValue(current, param, 255)) : current);

		if (['red', 'green', 'blue'].includes(Object.keys(updates)[0])) {
			const rgbUpdates = updates as RgbUpdates;
			const { red, green, blue, alpha } = this.extractRGBColor(color);

			return this.rgbToHexString({
				red: updateRgb(red, rgbUpdates.red),
				green: updateRgb(green, rgbUpdates.green),
				blue: updateRgb(blue, rgbUpdates.blue),
				alpha: updateAlpha(alpha, rgbUpdates.alpha),
			});
		} else {
			const hslUpdates = updates as HslUpdates;
			const { hue, saturation, lightness, alpha } = this.extractHSL(color);

			return this.hslToHexString({
				hue: change ? hslUpdates.hue ?? 0 : hue + (hslUpdates.hue ?? 0),
				saturation: updateHSL(saturation, hslUpdates.saturation),
				lightness: updateHSL(lightness, hslUpdates.lightness),
				alpha: updateAlpha(alpha, hslUpdates.alpha),
			});
		}
	}
}

function clamp(value: number, min: number, max: number) {
	return Math.min(Math.max(value, min), max);
}

const precision = 10;
const epsilon = 10 ** (-precision - 1);
export function fuzzyEquals(num1: number, num2: number): boolean {
	return Math.abs(num1 - num2) < epsilon;
}

export function fuzzyLessThan(num1: number, num2: number): boolean {
	return num1 < num2 && !fuzzyEquals(num1, num2);
}
export function fuzzyLessThanOrEquals(num1: number, num2: number): boolean {
	return num1 < num2 || fuzzyEquals(num1, num2);
}
function fuzzyRound(num: number): number {
	if (num > 0) {
		return fuzzyLessThan(num % 1, 0.5) ? Math.floor(num) : Math.ceil(num);
	} else {
		return fuzzyGreaterThan(num % 1, -0.5) ? Math.ceil(num) : Math.floor(num);
	}
}
export function fuzzyGreaterThan(num1: number, num2: number): boolean {
	return num1 > num2 && !fuzzyEquals(num1, num2);
}
